import { Paper, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { useBatteryInfoWithPollingQuery } from '@/features/resource/hooks/useBatteryInfoWithPollingQuery'
import { convertEnergyPower } from '@/utils/powerEnergyTransformations'

type SoCLiveReadoutProps = Readonly<{
  /** Battery control-port id used to poll SoC; null when the resource has no battery port. */
  controlPortId: string | null
  /** Latest power-consumption estimate in Watts (polled with the resource), or null. */
  estimatedPower: number | null
}>

/**
 * Live feedback surface for the SoC management tab: current power consumption and current SoC,
 * both refreshed in real time (SoC via the 5 s battery-info poll, power via the resource poll).
 * Target SoC and SoC history are intentionally NOT shown — neither is available to the UI.
 */
export function SoCLiveReadout({ controlPortId, estimatedPower }: SoCLiveReadoutProps) {
  const { t } = useTranslation()
  // Gate the poll on a real control-port id so we never hit `/batteries//battery-info`.
  const { batteryInfo, isError } = useBatteryInfoWithPollingQuery(
    { controlPortId: controlPortId ?? '' },
    { enabled: controlPortId !== null },
  )

  const powerLabel =
    estimatedPower === null
      ? t('resources.soc_management.readout.no_value')
      : convertEnergyPower(estimatedPower, 'kilowatts')
  // Distinguish a failing poll (surface "live data unavailable") from a genuine missing reading
  // ("N/A") — a silent N/A would otherwise hide backend/connectivity errors from the operator.
  const socLabel = getSocLabel()

  function getSocLabel(): string {
    if (isError) return t('resources.soc_management.readout.unavailable')
    if (batteryInfo === undefined) return t('resources.soc_management.readout.no_value')
    return `${Math.round(batteryInfo.stateOfCharge * 100)} %`
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="column" spacing={2}>
        <CustomTypography variant="h3">{t('resources.soc_management.readout.title')}</CustomTypography>
        <Stack direction="row" spacing={6}>
          <Readout label={t('resources.soc_management.readout.power_consumption')} value={powerLabel} />
          <Readout label={t('resources.soc_management.readout.state_of_charge')} value={socLabel} />
        </Stack>
      </Stack>
    </Paper>
  )
}

function Readout({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Stack direction="column" spacing={0.5}>
      <CustomTypography color="grey.500" variant="caption">
        {label}
      </CustomTypography>
      <CustomTypography variant="h4">{value}</CustomTypography>
    </Stack>
  )
}
